import React, { useMemo } from "react";

import { MetricsDashboardGraphDimension, MetricsDashboardGraphDimensionFormat, MetricsDashboardGraphTypeDisplayType } from "../../../__generated__/api";
import ReportFieldSelector from "../../../components/pickers/field-selector/ReportFieldSelector";
import FormGroup from "../../../components/theme/form-group/FormGroup";
import { Themes, useTheme } from "../../../contexts/Theme";
import Styles from "../Reports.module.scss";
import {
    CHART_COLORS,
    createDisplayFormatter,
    filter, formatDate, formatValueWithDisplay, getMetricValue, GraphProps,
    GraphSettingsProperties,
} from "./base";
import Chart from "./Chart";
import ColumnProperties from "./Column";
import Filters from "./Filters";
import Title from "./Title";
import createId from "../../../create-id";
import FormatOptions from "./FormatOptions";

export const COLORS_LIGHT = [
    "#4E6AFF",
    "#3ABA6D",
    "#102071",
    "#FF833D",
    "#BBE3FA",
    "#B6C1FF",
    "#82D398",
    "#3FABE5",
    "#FEA97A",
    "#C8D2DE",
];

export const COLORS_DARK = [
    "#768CFF",
    "#2D9D60",
    "#2842C7",
    "#FF833D",
    "#BBE3FA",
    "#B6C1FF",
    "#82D398",
    "#3FABE5",
    "#FEA97A",
    "#A1AFC0",
];

export function TimeSeriesProperties(props: GraphSettingsProperties) {
    const dimension = props.graph.dimensions?.firstElement;
    return (

        <div>

            <FormGroup
                label="Date Field"
                required
                size="small"
                helpDisplay="icon"
                helpText="What value do you want displayed in the count"
            >
                <ReportFieldSelector
                    value={dimension?.fieldId}
                    dataSource={props.graph.dataSource}
                    onChange={value => {
                        const dimensionCopy = { ...dimension };
                        dimensionCopy.fieldId = value?.field.id;
                        props.onChange({
                            ...props.graph,
                            dimensions: [dimensionCopy as MetricsDashboardGraphDimension],
                        });
                    }}
                />
            </FormGroup>

            <ColumnProperties
                field="metrics"
                label="Metrics"
                helpText="The list of values displayed by the ticket count."
                canSetAggregation
                canAddLabel
                canAddFormat
                {...props}
            />

            <Filters isAdmin={props.isAdmin} graph={props.graph} onChange={props.onChange} />

        </div>
    );
}

export default function TimeSeries(props: GraphProps) {
    const theme = useTheme();
    const className = useMemo(() => `color-${createId().substring(0, 10)}-`, []);

    const colors = theme.getTheme() === Themes.DARK ? COLORS_DARK : COLORS_LIGHT;

    const options = useMemo(() => {
        const dimension = props.graph.dimensions?.firstElement;
        if (!dimension) {
            return {};
        }
        const dateIndex = props.columnIndexMap[dimension.fieldId];

        const dataRows = filter(props.data.rows, props.graph.filters, props.columnIndexMap);

        const rows = dataRows.groupBy(row => {
            const date = new Date(row.values[dateIndex] as string);
            return formatDate(date, dimension.format ?? MetricsDashboardGraphDimensionFormat.YearMonthDay);
        });

        const metrics = props.graph.metrics ?? [];

        const metricDisplay = metrics.firstElement?.display;
        const groups: string[][][] = metrics.map(() => []);
        Object.entries(rows).forEach(([date, group]) => {
            metrics.forEach((metric, index) => {
                const metricIndex = props.columnIndexMap[metric.fieldId];
                const value = getMetricValue(group.map(row => row.values[metricIndex]).filterNull(), metric.aggregation);

                return groups[index].push([date, value?.toString() ?? "-"]);
            });
        });

        return {
            tooltip: {
                trigger: "axis",
                padding: theme.isV3 ? 0 : undefined,
                borderWidth: 0,
                formatter(params: {seriesName: string; name: string; color: string; value: string[]; seriesIndex: number}[]) {
                    const date = new Date(params.firstElement.value[0]).toLocaleDateString(undefined, {
                        dateStyle: "long",
                    });

                    // ${param.color}
                    const html = `<div class=${Styles["time-tooltip"]}>
                                <strong class=${Styles["time-tooltip-title"]}>${date}</strong>`;
                    return `${html}${
                        params.map((param, index) => {
                            const display = props.graph.metrics?.[param.seriesIndex].display;
                            const value = display
                                ? formatValueWithDisplay(
                                    parseInt(param.value[1], 10),
                                    display ?? MetricsDashboardGraphTypeDisplayType.Number,
                                )
                                : param.value[1];

                            return `<div class=${Styles["time-tooltip-row"]}>
                                <div class="${Styles["time-circle"]} ${className}${index}"></div>
                                <div class=${Styles["time-tooltip-row-title"]}>
                                    ${param.seriesName}
                                </div>
                                <div>${value}</div>
                            </div>`;
                        }).join("\n")}</div>`;
                },
                axisPointer: {
                    animation: false,
                },
            },
            yAxis: props.graph.metrics?.map(() => ({
                type: "value",
                axisLabel: {
                    color: CHART_COLORS.text(),
                    margin: 5,
                    padding: 0,
                    formatter: metricDisplay ? createDisplayFormatter(metricDisplay) : undefined,
                },
                splitLine: {
                    show: theme.isV3,
                    lineStyle: theme.isV3 ? {
                        type: "dashed",
                        color: CHART_COLORS.grid(),
                    } : undefined,
                },
            })),
            xAxis: {
                type: "time",
                axisLabel: {
                    color: CHART_COLORS.text(),
                },
                axisLine: {
                    lineStyle: {
                        color: CHART_COLORS.grid(),
                    },
                },
                splitLine: {
                    show: false,
                },
            },
            textStyle: theme.isV3 ? {
                textColor: "red",
            } : undefined,
            legend: theme.isV3 ? {
                bottom: "0",
                textStyle: {
                    color: CHART_COLORS.text(),
                },
                lineStyle: theme.isV3 ? {
                    color: "none",
                    inactiveColor: "none",
                } : undefined,
            } : {
                left: "2%",
            },
            grid: theme.isV3 ? {
                left: "10px",
                right: "10px",
                bottom: "40px",
                top: "10%",
                containLabel: true,
            } : {
                left: "40px",
                right: "40px",
                bottom: "10%",
                top: "20%",
            },
            dataZoom: [
                {
                    type: "inside",
                    // xAxisIndex: 0,
                    // minValueSpan: Time.DAY,
                    xAxisIndex: 0,
                    minSpan: 30,

                },
            ],

            series: props.graph.metrics?.map((metric, index) => {
                let { label } = metric;
                if (!label) {
                    const columnIndex = props.columnIndexMap[metric.fieldId];
                    const column = props.data.columns[columnIndex];
                    label = column?.field.name;
                }

                return {
                    name: label,
                    type: "line",
                    showSymbol: false,
                    smooth: theme.isV3,
                    data: groups[index],
                    yAxisIndex: 0,
                    color: theme.isV3 ? colors[index % colors.length] : undefined,
                    symbol: "roundRect",
                };
            }),

        };
    }, [props.data, props.graph, colors]);

    return (
        <div className={Styles["time-series"]}>
            <style nonce={window.NONCE}>
                {colors.map((color, index) => `
                    .${className}${index} {
                        background-color: ${color};
                    }
                `)}
            </style>
            <Title isAdmin={props.isAdmin} graph={props.graph} createMenu={props.createMenu} />

            <Chart options={options} />
        </div>
    );
}
